import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import PdfThumbnail from './PdfThumbnail';

/* PdfViewer — wraps PdfThumbnail with prev/next arrows and a page counter
 * so users can flip through engineering plans / insurance reports / etc.
 * directly inside the gallery tile.
 *
 * Designed to drop into any aspect-ratio container — fills the parent
 * `<div>`. Click on the page image (NOT the arrows) bubbles up so the
 * parent <a> can still open the original PDF in a new tab.
 *
 * Props:
 *   url            – PDF URL (absolute)
 *   onFail         – fallback when pdf.js fails
 *   showArrows     – 'hover' (default) | 'always' | 'never'
 *   showCounter    – 'auto' (default — only if numPages > 1) | 'always' | 'never'
 */

export default function PdfViewer({ url, onFail, showArrows = 'hover', showCounter = 'auto' }) {
  const [pageIndex, setPageIndex] = useState(1);
  const [numPages, setNumPages] = useState(1);

  // Reset to page 1 when the URL changes so swapping a gallery tile
  // doesn't accidentally render page 5 of the new doc.
  useEffect(() => { setPageIndex(1); }, [url]);

  const prev = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setPageIndex((p) => Math.max(1, p - 1));
  }, []);
  const next = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setPageIndex((p) => Math.min(numPages, p + 1));
  }, [numPages]);

  const onMeta = useCallback(({ numPages: n }) => {
    if (typeof n === 'number' && n > 0) setNumPages(n);
  }, []);

  const arrowsVisible = showArrows !== 'never' && numPages > 1;
  const arrowOpacityClass = showArrows === 'always'
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100';
  const counterVisible =
    showCounter === 'always'
    || (showCounter === 'auto' && numPages > 1);

  return (
    <div className="relative w-full h-full group">
      <PdfThumbnail
        url={url}
        pageIndex={pageIndex}
        onFail={onFail}
        onMeta={onMeta}
        className="w-full h-full object-contain"
        style={{ backgroundColor: '#F5F0E1' }}
        alt={`Page ${pageIndex} of ${numPages}`}
      />
      {/* PDF kind badge top-left, always visible */}
      <span
        className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-bold pointer-events-none"
        style={{ backgroundColor: '#DC2626', color: 'white', fontFamily: 'Manrope' }}
      >
        <FileText className="w-2.5 h-2.5" /> PDF
      </span>
      {/* Page counter top-right */}
      {counterVisible && (
        <span
          data-testid="pdf-viewer-counter"
          className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold pointer-events-none"
          style={{ backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', fontFamily: 'Manrope' }}
        >
          {pageIndex} / {numPages}
        </span>
      )}
      {/* Prev arrow */}
      {arrowsVisible && (
        <button
          type="button"
          data-testid="pdf-viewer-prev"
          aria-label="Previous page"
          onClick={prev}
          disabled={pageIndex <= 1}
          className={`absolute top-1/2 -translate-y-1/2 left-1 inline-flex items-center justify-center rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed ${arrowOpacityClass}`}
          style={{ width: 28, height: 28, backgroundColor: 'rgba(0,0,0,0.55)', color: 'white' }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      {/* Next arrow */}
      {arrowsVisible && (
        <button
          type="button"
          data-testid="pdf-viewer-next"
          aria-label="Next page"
          onClick={next}
          disabled={pageIndex >= numPages}
          className={`absolute top-1/2 -translate-y-1/2 right-1 inline-flex items-center justify-center rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed ${arrowOpacityClass}`}
          style={{ width: 28, height: 28, backgroundColor: 'rgba(0,0,0,0.55)', color: 'white' }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
